import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, FileText, Mail, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicUpload = () => {
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    notes: ""
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error("Please attach a hearing notice file");
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("client_name", formData.client_name);
      formDataToSend.append("client_email", formData.client_email);
      formDataToSend.append("notes", formData.notes);
      formDataToSend.append("file", file);

      const response = await axios.post(`${API}/upload`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success(response.data.message);
      
      // Reset form
      setFormData({ client_name: "", client_email: "", notes: "" });
      setFile(null);
      document.getElementById("file-input").value = "";
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload notice. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      if (["pdf", "jpg", "jpeg", "png"].includes(fileExt)) {
        setFile(selectedFile);
      } else {
        toast.error("Please upload a PDF, JPG, or PNG file");
        e.target.value = "";
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8]">
      {/* Header */}
      <div className="header-gradient text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <img 
            src="/coverdocket-logo.png" 
            alt="CoverDocket Logo" 
            className="h-16 w-auto"
          />
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">CoverDocket</h1>
            <p className="mt-1 text-lg opacity-90">Your Smart Hearing-Coverage System</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="glass-card border-0">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-semibold text-[#0A1128]">
              Upload a Hearing Notice
            </CardTitle>
            <CardDescription className="text-base">
              Submit your court hearing notice and we'll handle the rest. You'll receive confirmation by email once an attorney is assigned.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Client Name */}
              <div className="space-y-2">
                <Label htmlFor="client_name" className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client Name/or Firm Name
                </Label>
                <Input
                  id="client_name"
                  data-testid="client-name-input"
                  type="text"
                  required
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="e.g., John Smith or Smith Legal Group"
                  className="h-11"
                />
              </div>

              {/* Client Email */}
              <div className="space-y-2">
                <Label htmlFor="client_email" className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Your Email
                </Label>
                <Input
                  id="client_email"
                  data-testid="client-email-input"
                  type="email"
                  required
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="your.email@example.com"
                  className="h-11"
                />
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label htmlFor="file-input" className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Upload Notice (PDF/JPG/PNG)
                </Label>
                <div className="relative">
                  <Input
                    id="file-input"
                    data-testid="file-input"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    required
                    onChange={handleFileChange}
                    className="h-11 cursor-pointer"
                  />
                  {file && (
                    <p className="mt-2 text-sm text-[#6497B1] flex items-center gap-1">
                      <Upload className="w-4 h-4" />
                      Selected: {file.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm font-medium">
                  Notes (Optional)
                </Label>
                <Textarea
                  id="notes"
                  data-testid="notes-textarea"
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional information or special instructions..."
                  className="resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="submit-notice-button"
                disabled={loading}
                className="w-full h-12 text-base btn-primary font-medium"
              >
                {loading ? "Submitting..." : "Submit Notice"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Join Attorney Network Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => window.location.href = '/register'}
            variant="outline"
            className="btn-secondary text-white h-12 px-8"
            data-testid="join-network-button"
          >
            Join Our Coverage Network
          </Button>
          <p className="text-sm text-gray-600 mt-2">Are you an attorney? Register to receive coverage opportunities</p>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
            <h3 className="text-lg font-semibold text-[#0A1128] mb-3">Contact Us</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p className="font-medium">Phone: (866) 357-5426 or locally (561) 264-7188</p>
              <p>Email: therecruitingpartner@gmail.com</p>
              <p>9169 W State St #826, Garden City, ID 83714</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">CoverDocket™ | 2025 All Rights Reserved</p>
        </div>
      </div>
    </div>
  );
};

export default PublicUpload;